import React, { useEffect, useRef, useState } from "react";
import { useAuth } from "@/lib/auth";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Link } from "react-router-dom";
import Layout from "@/components/Layout";
import {
  Upload, Trash2, Phone, Mail, MapPin, Instagram, Lock, Sparkles, Loader2, Save,
} from "lucide-react";

const MAX_LOGO_BYTES = 250 * 1024; // 250KB hard cap (matches backend limit)

export default function Profile() {
  const { user } = useAuth();
  const isPro = user?.plan === "pro";
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [logoBase64, setLogoBase64] = useState(null); // pure base64 (no data: prefix)
  const [contacts, setContacts] = useState({
    phone: "", email: "", address: "", instagram: "",
  });
  const fileRef = useRef(null);

  useEffect(() => {
    api.get("/me/branding")
      .then((res) => {
        setLogoBase64(res.data.brand_logo_base64 || null);
        setContacts({
          phone: res.data.brand_contacts?.phone || "",
          email: res.data.brand_contacts?.email || "",
          address: res.data.brand_contacts?.address || "",
          instagram: res.data.brand_contacts?.instagram || "",
        });
      })
      .catch(() => toast.error("Impossibile caricare le impostazioni di branding"))
      .finally(() => setLoading(false));
  }, []);

  const onPickLogo = () => {
    if (!isPro) return;
    fileRef.current?.click();
  };

  const onLogoFile = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!["image/png", "image/jpeg", "image/jpg", "image/webp"].includes(file.type)) {
      toast.error("Formato non supportato. Usa PNG, JPG o WEBP.");
      return;
    }
    if (file.size > MAX_LOGO_BYTES) {
      toast.error("Logo troppo grande. Caricalo sotto i 250 KB.");
      return;
    }
    const reader = new FileReader();
    reader.onload = (ev) => {
      const dataUrl = ev.target.result;
      const b64 = dataUrl.split(",")[1];
      setLogoBase64(b64);
    };
    reader.readAsDataURL(file);
  };

  const removeLogo = () => setLogoBase64(null);

  const onSave = async () => {
    if (!isPro) return;
    if (contacts.email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(contacts.email)) {
      toast.error("Email contatto non valida.");
      return;
    }
    setSaving(true);
    try {
      await api.put("/me/branding", {
        brand_logo_base64: logoBase64,
        brand_contacts: contacts,
      });
      toast.success("Branding salvato. Sarà applicato ai prossimi PDF.");
    } catch (err) {
      const status = err?.response?.status;
      const detail = err?.response?.data?.detail;
      if (status === 403) {
        toast.error("Funzione disponibile solo per il piano Pro.");
      } else if (typeof detail === "string") {
        toast.error(detail);
      } else {
        toast.error("Errore durante il salvataggio.");
      }
    } finally {
      setSaving(false);
    }
  };

  const logoSrc = logoBase64 ? `data:image/png;base64,${logoBase64}` : null;

  return (
    <Layout>
      <div className="mb-10 animate-fade-up" data-testid="profile-page">
        <p className="text-xs tracking-[0.3em] uppercase text-[#C5A059] mb-3">Profilo</p>
        <h1 className="font-serif-display text-4xl sm:text-5xl text-[#1A1A1A] mb-3">
          Branding Salone
        </h1>
        <p className="text-[#737373] max-w-2xl">
          Personalizza il PDF dei tuoi protocolli con il logo del salone e i tuoi
          contatti. Il branding apparirà sull'header e sul footer di ogni report
          che condividi con i clienti.
        </p>
      </div>

      {/* Banner upgrade per non-PRO */}
      {!isPro && (
        <div
          className="rounded-2xl border border-[#C5A059]/40 bg-gradient-to-br from-[#FAF6EE] to-white p-6 lg:p-8 mb-10 animate-fade-up"
          data-testid="branding-upgrade-banner"
        >
          <div className="flex flex-col lg:flex-row lg:items-center gap-5">
            <div className="w-12 h-12 rounded-xl bg-white border border-[#E5E5E0] flex items-center justify-center shrink-0">
              <Lock className="w-5 h-5 text-[#C5A059]" />
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-xs tracking-[0.2em] uppercase text-[#C5A059] font-medium mb-1">
                Funzionalità Pro
              </p>
              <p className="font-serif-display text-2xl text-[#1A1A1A] leading-tight">
                Sblocca il branding personalizzato con Pro
              </p>
              <p className="text-sm text-[#737373] mt-2">
                Aggiungi il logo del tuo salone e i tuoi contatti ai PDF dei
                protocolli. Trasforma ogni report in un asset di marketing.
              </p>
            </div>
            <Link
              to="/piani"
              data-testid="branding-upgrade-cta"
              className="h-12 px-6 rounded-xl bg-[#1A1A1A] hover:bg-[#C5A059] text-white font-medium inline-flex items-center justify-center gap-2 transition-all shrink-0"
            >
              <Sparkles className="w-4 h-4" />
              Passa a Pro
            </Link>
          </div>
        </div>
      )}

      {loading ? (
        <div className="rounded-2xl border border-[#E5E5E0] bg-white p-10 flex items-center justify-center">
          <Loader2 className="w-6 h-6 text-[#C5A059] animate-spin" />
        </div>
      ) : (
        <div className={`grid lg:grid-cols-2 gap-6 mb-10 ${!isPro ? "opacity-60 pointer-events-none select-none" : ""}`}>
          {/* Logo card */}
          <div className="rounded-2xl border border-[#E5E5E0] bg-white p-6 lg:p-8" data-testid="branding-logo-card">
            <p className="text-xs tracking-[0.2em] uppercase text-[#C5A059] mb-1 font-medium">
              Logo del salone
            </p>
            <h2 className="font-serif-display text-2xl text-[#1A1A1A] mb-1">
              Identità visiva
            </h2>
            <p className="text-sm text-[#737373] mb-5">
              PNG, JPG o WEBP. Max 250 KB. Verrà ridimensionato a 60px di altezza nel PDF.
            </p>

            <div className="rounded-xl border border-dashed border-[#E5E5E0] bg-[#FAFAFA] p-6 flex items-center justify-center min-h-[140px]">
              {logoSrc ? (
                <img
                  src={logoSrc}
                  alt="Logo salone"
                  className="max-h-24 max-w-full object-contain"
                  data-testid="branding-logo-preview"
                />
              ) : (
                <div className="text-center">
                  <Upload className="w-7 h-7 text-[#C5A059] mx-auto mb-2" />
                  <p className="text-sm text-[#737373]">Nessun logo caricato</p>
                </div>
              )}
            </div>

            <div className="mt-4 flex flex-wrap gap-3">
              <button
                type="button"
                onClick={onPickLogo}
                disabled={!isPro}
                className="h-11 px-5 rounded-xl border border-[#E5E5E0] hover:border-[#C5A059] text-sm font-medium text-[#1A1A1A] inline-flex items-center gap-2 transition-all disabled:opacity-40 disabled:cursor-not-allowed"
                data-testid="branding-logo-upload-btn"
              >
                <Upload className="w-4 h-4" />
                {logoSrc ? "Sostituisci" : "Carica logo"}
              </button>
              {logoSrc && (
                <button
                  type="button"
                  onClick={removeLogo}
                  disabled={!isPro}
                  className="h-11 px-5 rounded-xl border border-[#E5E5E0] hover:border-[#1A1A1A] text-sm font-medium text-[#737373] hover:text-[#1A1A1A] inline-flex items-center gap-2 transition-all disabled:opacity-40 disabled:cursor-not-allowed"
                  data-testid="branding-logo-remove-btn"
                >
                  <Trash2 className="w-4 h-4" />
                  Rimuovi
                </button>
              )}
              <input
                ref={fileRef}
                type="file"
                accept="image/png,image/jpeg,image/webp"
                onChange={onLogoFile}
                className="hidden"
                data-testid="branding-logo-input"
              />
            </div>
          </div>

          {/* Contacts card */}
          <div className="rounded-2xl border border-[#E5E5E0] bg-white p-6 lg:p-8" data-testid="branding-contacts-card">
            <p className="text-xs tracking-[0.2em] uppercase text-[#C5A059] mb-1 font-medium">
              Contatti del salone
            </p>
            <h2 className="font-serif-display text-2xl text-[#1A1A1A] mb-1">
              Informazioni footer
            </h2>
            <p className="text-sm text-[#737373] mb-5">
              Compila solo i campi che vuoi mostrare nel footer del PDF.
            </p>

            <div className="space-y-4">
              <Field
                icon={Phone}
                label="Telefono"
                value={contacts.phone}
                onChange={(v) => setContacts((c) => ({ ...c, phone: v }))}
                placeholder="+39 02 1234567"
                testid="branding-phone"
                disabled={!isPro}
              />
              <Field
                icon={Mail}
                label="Email"
                value={contacts.email}
                onChange={(v) => setContacts((c) => ({ ...c, email: v }))}
                placeholder="info@iltuosalone.it"
                testid="branding-email"
                type="email"
                disabled={!isPro}
              />
              <Field
                icon={MapPin}
                label="Indirizzo"
                value={contacts.address}
                onChange={(v) => setContacts((c) => ({ ...c, address: v }))}
                placeholder="Via Roma 1, 20121 Milano"
                testid="branding-address"
                disabled={!isPro}
              />
              <Field
                icon={Instagram}
                label="Instagram"
                value={contacts.instagram}
                onChange={(v) => setContacts((c) => ({ ...c, instagram: v }))}
                placeholder="@iltuosalone"
                testid="branding-instagram"
                disabled={!isPro}
              />
            </div>
          </div>
        </div>
      )}

      {/* Save bar */}
      <div className="rounded-2xl border border-[#E5E5E0] bg-white p-6 lg:p-7 flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
        <div>
          <p className="text-sm font-medium text-[#1A1A1A]">
            Le modifiche vengono applicate ai PDF generati da ora in poi
          </p>
          <p className="text-xs text-[#737373] mt-1">
            Il marchio "Powered by AI TricoProtocol™" resta sempre presente nel footer.
          </p>
        </div>
        <button
          type="button"
          onClick={onSave}
          disabled={!isPro || saving || loading}
          className="h-12 px-6 rounded-xl bg-[#1A1A1A] hover:bg-[#C5A059] text-white font-medium inline-flex items-center justify-center gap-2 transition-all disabled:opacity-40 disabled:cursor-not-allowed min-w-[180px]"
          data-testid="branding-save-btn"
        >
          {saving ? (
            <>
              <Loader2 className="w-4 h-4 animate-spin" />
              Salvataggio...
            </>
          ) : (
            <>
              <Save className="w-4 h-4" />
              Salva branding
            </>
          )}
        </button>
      </div>
    </Layout>
  );
}

function Field({ icon: Icon, label, value, onChange, placeholder, testid, type = "text", disabled }) {
  return (
    <label className="block">
      <span className="text-xs uppercase tracking-wider text-[#737373] font-medium flex items-center gap-1.5 mb-1.5">
        <Icon className="w-3.5 h-3.5" />
        {label}
      </span>
      <input
        type={type}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        disabled={disabled}
        className="w-full h-11 px-4 rounded-xl border border-[#E5E5E0] focus:border-[#C5A059] focus:outline-none transition-colors text-sm text-[#1A1A1A] bg-white disabled:bg-[#FAFAFA] disabled:cursor-not-allowed"
        data-testid={testid}
      />
    </label>
  );
}
